// Falling blocks (M10.1). PURE (ENG-1): sand, gravel, and concrete powder fall
// when unsupported, land on the first solid cell, and either re-place themselves
// or drop as an item if the landing cell is occupied. Spawned by the host when a
// gravity block loses its support (a block update), so gravity — like Minecraft —
// only runs near the player, never during bulk generation.
import { FALLING } from '../config';

export interface FallingBlock {
  readonly id: number;
  /** The block state carried down. */
  readonly state: number;
  /** Column (block-centred x,z); `y` is the continuous block-bottom position. */
  x: number;
  y: number;
  z: number;
  vy: number;
}

/** Just enough world for the falling-block physics + landing. */
export interface FallingWorld {
  /** Can a falling block pass through (x,y,z)? Air / fluid / washable plant. */
  isPassable(x: number, y: number, z: number): boolean;
  /** Place the landed block; false if the cell is occupied (→ drop as item). */
  place(x: number, y: number, z: number, state: number): boolean;
  /** Scatter the block as an item entity (landing blocked). */
  dropItem(state: number, x: number, y: number, z: number): void;
}

export class FallingBlockSystem {
  readonly blocks: FallingBlock[] = [];
  private nextId = 1;

  get count(): number { return this.blocks.length; }

  /** Begin a fall from block cell (bx,by,bz). Coordinates are block indices. */
  spawn(state: number, bx: number, by: number, bz: number): FallingBlock {
    const fb: FallingBlock = { id: this.nextId++, state, x: bx + 0.5, y: by, z: bz + 0.5, vy: 0 };
    this.blocks.push(fb);
    return fb;
  }

  clear(): void { this.blocks.length = 0; }

  /** Advance every falling block one tick; land those that reach a solid cell. */
  tick(world: FallingWorld): void {
    for (let i = this.blocks.length - 1; i >= 0; i--) {
      const b = this.blocks[i]!;
      b.vy = Math.max(-FALLING.MAX_SPEED, b.vy - FALLING.GRAVITY);
      const ny = b.y + b.vy;
      const bx = Math.floor(b.x), bz = Math.floor(b.z);
      const cellY = Math.floor(ny);
      if (world.isPassable(bx, cellY, bz)) {
        b.y = ny; // keep falling
        continue;
      }
      // Landed: rest on top of the obstacle (cellY+1). Re-place, else drop.
      const landY = cellY + 1;
      if (!world.place(bx, landY, bz, b.state)) world.dropItem(b.state, bx, landY, bz);
      this.blocks.splice(i, 1);
    }
  }
}
